import { useEffect, useState } from 'react'
import { useDocumentPDF } from '../api'

/**
 * The presigned PDF URL turned into a blob URL, as the standalone PDF page does. Keyed on the
 * presigned URL so moving to the next document never shows the previous one's blob.
 */
export function useDocumentPdfFile(documentKey: string) {
  const { data: pdfUrl, isFetching } = useDocumentPDF(documentKey)
  const [blob, setBlob] = useState<{ source: string; url: string } | null>(null)
  const [failedSource, setFailedSource] = useState<string | null>(null)

  useEffect(() => {
    if (!pdfUrl) return

    let isCurrent = true
    let objectUrl: string | null = null

    fetch(pdfUrl, { credentials: 'include' })
      .then(async (response) => {
        if (response.status !== 200) throw new Error(`PDF request failed with ${response.status}`)
        const data = await response.blob()
        if (!isCurrent) return
        objectUrl = URL.createObjectURL(data)
        setBlob({ source: pdfUrl, url: objectUrl })
      })
      .catch(() => {
        if (isCurrent) setFailedSource(pdfUrl)
      })

    return () => {
      isCurrent = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [pdfUrl])

  const blobUrl = blob && blob.source === pdfUrl ? blob.url : null
  const hasBlobFailed = !!pdfUrl && failedSource === pdfUrl

  return {
    // Falls back to the presigned URL when the blob fetch fails, like the standalone PDF page.
    file: blobUrl ?? (hasBlobFailed ? pdfUrl : null),
    isLoading: isFetching || (!!pdfUrl && !blobUrl && !hasBlobFailed),
  }
}
